"use client";

import React, { createContext, useContext, useState, useMemo, useCallback } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import api from "@/lib/axios";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/store/useStore";
import { RoleUtils } from "@/lib/constants/roles";
import { clientDebugLog } from "@/lib/debug/clientDebug";
import type {
  InfiniteData,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";

// ===== TYPES =====
export interface Client {
  id: string;
  name: string;
  account_uid?: string;
  account_name?: string;
  account_status?: string;
}

interface PaginatedClientsResponse {
  data: Client[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
  };
}

function isCompletePagination(
  p: unknown
): p is PaginatedClientsResponse["pagination"] {
  return (
    !!p &&
    typeof p === "object" &&
    typeof (p as PaginatedClientsResponse["pagination"]).total === "number" &&
    typeof (p as PaginatedClientsResponse["pagination"]).page === "number" &&
    typeof (p as PaginatedClientsResponse["pagination"]).limit === "number" &&
    typeof (p as PaginatedClientsResponse["pagination"]).totalPages ===
      "number" &&
    typeof (p as PaginatedClientsResponse["pagination"]).hasNextPage ===
      "boolean"
  );
}

function normalizeClientsResponse(
  raw: unknown,
  page: number
): PaginatedClientsResponse {
  if (Array.isArray(raw)) {
    const data = raw as Client[];
    return {
      data,
      pagination: {
        total: data.length,
        page,
        limit: data.length || 1,
        totalPages: 1,
        hasNextPage: false,
      },
    };
  }

  if (raw && typeof raw === "object" && Array.isArray((raw as { data?: unknown }).data)) {
    const body = raw as {
      data: Client[];
      pagination?: unknown;
    };
    const data = body.data;
    if (isCompletePagination(body.pagination)) {
      return { data, pagination: body.pagination };
    }
    return {
      data,
      pagination: {
        total: data.length,
        page,
        limit: data.length || 1,
        totalPages: 1,
        hasNextPage: false,
      },
    };
  }

  return {
    data: [],
    pagination: {
      total: 0,
      page,
      limit: 0,
      totalPages: 0,
      hasNextPage: false,
    },
  };
}

// The shape of the context - now using Zustand as single source of truth
type ClientContextType = {
  // From useInfiniteQuery, passed directly to consumers
  clientsQuery: UseInfiniteQueryResult<
    InfiniteData<PaginatedClientsResponse, unknown>,
    Error
  >;
  // Derived, flattened list of all clients fetched so far
  allClients: Client[];
  // State for the search functionality
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  // Client selection now maps to Zustand store (maintaining API compatibility)
  selectedClients: string[]; // Maps to store.selectedClientIds
  setSelectedClients: (clients: string[]) => void; // Maps to store.setSelectedClientIds
  // Function to fetch all clients (for Select All)
  fetchAllClients: (ignoreSearch?: boolean) => Promise<Client[]>;
  // Total count of all clients in the database
  totalClientsCount: number;
  // All clients (not paginated) for Select All functionality
  allClientsForSelectAll: Client[];
  // Loading state for fetching all clients
  isLoadingAllClients: boolean;
  // Function to clear client selection state
  clearClientSelection: () => void;
};

const ClientContext = createContext<ClientContextType | undefined>(undefined);

// Custom debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export function ClientProvider({ children }: { children: React.ReactNode }) {
  const { accessToken, user } = useAuth();
  const { 
    selectedClientIds, 
    setSelectedClientIds, 
    clearClientSelection: clearStoreClientSelection,
    hydrateClients
  } = useStore();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [allClientsForSelectAll, setAllClientsForSelectAll] = useState<Client[]>([]);
  const [isLoadingAllClients, setIsLoadingAllClients] = useState(false);
  const [previousUserId, setPreviousUserId] = useState<string | null>(null);
  const [hasTriggeredAdminFetch, setHasTriggeredAdminFetch] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  React.useEffect(() => {
    clientDebugLog("client_provider_init", {
      hasUser: !!user,
      userId: user?.id || null,
      hasAccessToken: !!accessToken,
    });
  }, [user?.id, accessToken, user]);

  // Handle client selection state on user changes
  React.useEffect(() => {
    const currentUserId = user?.id || null;
    
    // If this is the first time we're setting the user (page load/refresh)
    if (previousUserId === null) {
      // Restore client selection from localStorage for the current user
      if (currentUserId) {
        hydrateClients();
      }
      setPreviousUserId(currentUserId);
      return;
    }
    
    // If user actually changed (login/logout or switching users)
    if (previousUserId !== currentUserId) {
      setAllClientsForSelectAll([]);
      setSearchQuery("");
      setHasTriggeredAdminFetch(false); // Reset the flag for new user
      clearStoreClientSelection(); // Clear the Zustand store
      
      // Restore selection for new user if they exist
      if (currentUserId) {
        hydrateClients();
      }
      
      setPreviousUserId(currentUserId);
    }
  }, [user?.id, clearStoreClientSelection, hydrateClients, previousUserId]);

  // Centralized data fetching using useInfiniteQuery
  const clientsQuery = useInfiniteQuery<PaginatedClientsResponse, Error>({
    queryKey: ["clients-infinite", debouncedSearchQuery],
    queryFn: async ({ pageParam = 1 }) => {
      const startedAt = performance.now();
      const requestUrl = `/api/clients?search=${encodeURIComponent(
        debouncedSearchQuery
      )}&page=${pageParam}&limit=5000`;
      clientDebugLog("clients_query_start", {
        pageParam,
        requestUrl,
        hasAccessToken: !!accessToken,
      });
      try {
        const response = await api.get(
          requestUrl,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        const normalized = normalizeClientsResponse(response.data, pageParam as number);
        clientDebugLog("clients_query_success", {
          pageParam,
          durationMs: Math.round(performance.now() - startedAt),
          resultCount: normalized.data.length,
          total: normalized.pagination.total,
          hasNextPage: normalized.pagination.hasNextPage,
        });
        return normalized;
      } catch (error) {
        const message = error instanceof Error ? error.message : "unknown error";
        clientDebugLog(
          "clients_query_error",
          {
            pageParam,
            durationMs: Math.round(performance.now() - startedAt),
            message,
          },
          "error"
        );
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage
        ? lastPage.pagination.page + 1
        : undefined;
    },
    enabled: !!accessToken, // Only fetch when authenticated
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // A flattened and memoized list of all clients from all pages
  const allClients = useMemo(() => {
    return clientsQuery.data?.pages.flatMap((page) => page.data) ?? [];
  }, [clientsQuery.data]);

  // Get total count from the first page response
  const totalClientsCount = useMemo(() => {
    return clientsQuery.data?.pages[0]?.pagination.total ?? 0;
  }, [clientsQuery.data]);

  // Populate allClientsForSelectAll with clients from the infinite query
  React.useEffect(() => {
    if (user && allClients.length > 0) {
      // For admin users, use all clients from the fetched data
      // For non-admin users, use their assigned clients from the fetched data
      setAllClientsForSelectAll(allClients);
      clientDebugLog("sync_all_clients_for_select_all", {
        allClientsCount: allClients.length,
        userId: user.id,
      });
    }
  }, [user, allClients]);

  // Function to fetch all clients for Select All functionality
  const fetchAllClients = useCallback(async (ignoreSearch: boolean = false): Promise<Client[]> => {
    if (!accessToken) return [];
    
    setIsLoadingAllClients(true);
    const startedAt = performance.now();
    clientDebugLog("fetch_all_clients_start", {
      ignoreSearch,
      searchQuery: debouncedSearchQuery,
      hasAccessToken: !!accessToken,
    });
    try {
      // Use empty search if ignoreSearch is true (for admin initialization)
      const searchParam = ignoreSearch ? '' : encodeURIComponent(debouncedSearchQuery);
      
      // Fetch all clients with a high limit to get everything in one request
      const response = await api.get(
        `/api/clients?search=${searchParam}&page=1&limit=5000`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      
      const body = response.data;
      const allClientsData = Array.isArray(body)
        ? body
        : (body as PaginatedClientsResponse)?.data || [];
      setAllClientsForSelectAll(allClientsData);
      clientDebugLog("fetch_all_clients_success", {
        ignoreSearch,
        durationMs: Math.round(performance.now() - startedAt),
        resultCount: allClientsData.length,
      });
      return allClientsData;
    } catch (error) {
      console.error('Error fetching all clients:', error);
      clientDebugLog(
        "fetch_all_clients_error",
        {
          ignoreSearch,
          durationMs: Math.round(performance.now() - startedAt),
          message: error instanceof Error ? error.message : "unknown error",
        },
        "error"
      );
      return [];
    } finally {
      setIsLoadingAllClients(false);
      clientDebugLog("fetch_all_clients_finally", {
        ignoreSearch,
        isLoadingAllClients: false,
      });
    }
  }, [accessToken, debouncedSearchQuery]);

  // Function to clear client selection state
  const clearClientSelection = useCallback(() => {
    setAllClientsForSelectAll([]);
    setSearchQuery("");
    clearStoreClientSelection(); // Clear the Zustand store
  }, [clearStoreClientSelection]);

  // For admin users, also populate from fetchAllClients if infinite query is empty
  React.useEffect(() => {
    if (user && RoleUtils.isAdmin(user.roles) && allClientsForSelectAll.length === 0 && allClients.length === 0 && !hasTriggeredAdminFetch) {
      clientDebugLog("admin_fallback_fetch_triggered", {
        userId: user.id,
        allClientsCount: allClients.length,
        allClientsForSelectAllCount: allClientsForSelectAll.length,
      });
      // If admin user has no clients loaded, trigger a fetch (only once)
      const loadAdminClients = async () => {
        try {
          setHasTriggeredAdminFetch(true);
          await fetchAllClients(true);
        } catch (error) {
          console.error('Error loading admin clients:', error);
        }
      };
      loadAdminClients();
    }
  }, [user, allClientsForSelectAll.length, allClients.length, fetchAllClients, hasTriggeredAdminFetch]);

  React.useEffect(() => {
    clientDebugLog("client_state_snapshot", {
      userId: user?.id || null,
      isAdmin: !!(user && RoleUtils.isAdmin(user.roles)),
      allClientsCount: allClients.length,
      allClientsForSelectAllCount: allClientsForSelectAll.length,
      totalClientsCount,
      isLoadingQuery: clientsQuery.isLoading,
      isFetchingQuery: clientsQuery.isFetching,
      isLoadingAllClients,
      hasTriggeredAdminFetch,
    });
  }, [
    user?.id,
    user,
    allClients.length,
    allClientsForSelectAll.length,
    totalClientsCount,
    clientsQuery.isLoading,
    clientsQuery.isFetching,
    isLoadingAllClients,
    hasTriggeredAdminFetch,
  ]);

  const value: ClientContextType = {
    clientsQuery,
    allClients,
    searchQuery,
    setSearchQuery,
    // Map ClientContext API to Zustand store (single source of truth)
    selectedClients: selectedClientIds,
    setSelectedClients: setSelectedClientIds,
    fetchAllClients,
    totalClientsCount,
    allClientsForSelectAll,
    isLoadingAllClients,
    clearClientSelection,
  };

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
}
